import React, { useEffect, useState } from 'react';
import {
    Table,
    Button,
    Tag,
    Space,
    Modal,
    Input,
    message,
    Card,
    Typography,
    Badge,
    Image,
    Tabs,
} from 'antd';
import {
    CheckOutlined,
    CloseOutlined,
    PictureOutlined,
    ExclamationCircleOutlined,
} from '@ant-design/icons';
import { expenseAPI } from '../../services/api';
import dayjs from 'dayjs';
import { getReceiptUrl } from '../../utils/urlConfig';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { confirm } = Modal;

const ExpenseApprovalPage = () => {
    const [pendingExpenses, setPendingExpenses] = useState([]);
    const [approvedExpenses, setApprovedExpenses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [rejectModalVisible, setRejectModalVisible] = useState(false);
    const [selectedExpense, setSelectedExpense] = useState(null);
    const [rejectionReason, setRejectionReason] = useState('');
    const [activeTab, setActiveTab] = useState('pending');

    useEffect(() => {
        fetchExpenses();
    }, [activeTab]);

    const fetchExpenses = async () => {
        setLoading(true);
        try {
            const status = activeTab === 'pending' ? 'pm_pending' : 'finance_pending';
            const response = await expenseAPI.getAll({
                status,
                for_pm_approval: 'true' // ⭐ PM 필터 활성화
            });

            if (activeTab === 'pending') {
                setPendingExpenses(response.data.expenses);
            } else {
                setApprovedExpenses(response.data.expenses);
            }
        } catch (error) {
            message.error('경비 목록 조회 실패');
            console.error('Fetch expenses error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = (record) => {
        confirm({
            title: '경비 승인',
            icon: <ExclamationCircleOutlined />,
            width: 600,
            content: (
                <div>
                    <div style={{ marginBottom: 16 }}>
                        <p><strong>신청자:</strong> {record.user?.name} ({record.user?.employee_id})</p>
                        <p><strong>프로젝트:</strong> {record.project?.name}</p>
                        <p><strong>지출일:</strong> {dayjs(record.expense_date).format('YYYY-MM-DD')}</p>
                        <p><strong>구분:</strong> <Tag color="blue">{categoryMap[record.category] || record.category}</Tag></p>
                        <p><strong>지출처:</strong> {record.vendor}</p>
                        <p><strong>사용목적:</strong> {record.purpose}</p>
                        <p><Text strong style={{ fontSize: 16 }}>금액: {parseInt(record.amount || 0).toLocaleString()}원</Text></p>
                    </div>

                    {/* 영수증 표시 */}
                    {record.receipt_url && (
                        <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#f8f9fa', borderRadius: 6 }}>
                            <div style={{ marginBottom: 8, fontWeight: 'bold' }}>📄 첨부된 영수증:</div>
                            <Image
                                src={getReceiptUrl(record.receipt_url)}
                                alt="영수증"
                                style={{ maxWidth: '100%', maxHeight: 300 }}
                                placeholder={<PictureOutlined />}
                                fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
                                preview={{
                                    mask: (
                                        <div>
                                            <PictureOutlined style={{ marginRight: 4 }} />
                                            크게 보기
                                        </div>
                                    )
                                }}
                            />
                        </div>
                    )}

                    {/* 계좌 정보 */}
                    <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#e6f7ff', borderRadius: 6 }}>
                        <div style={{ marginBottom: 8, fontWeight: 'bold' }}>💳 입금 정보:</div>
                        <p><strong>은행:</strong> {record.bank_name || '-'}</p>
                        <p><strong>계좌:</strong> {record.bank_account || '-'}</p>
                        <p><strong>예금주:</strong> {record.account_holder || '-'}</p>
                    </div>
                    
                    <div style={{ textAlign: 'center', marginBottom: 16 }}>
                        <Text strong style={{ fontSize: 16 }}>위 경비를 승인하시겠습니까?</Text>
                    </div>
                    
                    <div style={{ padding: 8, background: '#fff7e6', borderRadius: 4 }}>
                        <Text type="warning" style={{ fontSize: 12 }}>
                            💡 승인 후 재무팀에서 위 계좌로 입금 처리합니다.
                        </Text>
                    </div>
                </div>
            ),
            okText: '승인',
            cancelText: '취소',
            onOk: async () => {
                try {
                    await expenseAPI.pmApprove(record.id, { action: 'approve' });
                    message.success('경비가 승인되었습니다');
                    fetchExpenses();
                } catch (error) {
                    message.error(error.response?.data?.message || '승인 처리 실패');
                }
            },
        });
    };

    const handleReject = async () => {
        if (!rejectionReason.trim()) {
            message.error('반려 사유를 입력해주세요');
            return;
        }

        try {
            await expenseAPI.pmApprove(selectedExpense.id, {
                action: 'reject',
                rejection_reason: rejectionReason,
            });
            message.success('경비가 반려되었습니다');
            setRejectModalVisible(false);
            setRejectionReason('');
            setSelectedExpense(null);
            fetchExpenses();
        } catch (error) {
            message.error(error.response?.data?.message || '반려 처리 실패');
        }
    };

    const categoryMap = {
        meal: '식비',
        transport: '교통비',
        accommodation: '숙박비',
        supplies: '소모품',
        communication: '통신비',
        entertainment: '접대비',
        other: '기타',
    };

    const baseColumns = [
        {
            title: '신청일',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 100,
            render: (date) => dayjs(date).format('MM-DD'),
        },
        {
            title: '지출일',
            dataIndex: 'expense_date',
            key: 'expense_date',
            width: 120,
            render: (date) => dayjs(date).format('YYYY-MM-DD'),
        },
        {
            title: '신청자',
            dataIndex: ['user', 'name'],
            key: 'user',
            width: 100,
        },
        {
            title: '프로젝트',
            dataIndex: ['project', 'name'],
            key: 'project',
            ellipsis: true,
        },
        {
            title: '구분',
            dataIndex: 'category',
            key: 'category',
            width: 100,
            render: (category) => (
                <Tag color="blue">{categoryMap[category] || category}</Tag>
            ),
        },
        {
            title: '지출처',
            dataIndex: 'vendor',
            key: 'vendor',
            width: 150,
            ellipsis: true,
        },
        {
            title: '금액',
            dataIndex: 'amount',
            key: 'amount',
            width: 120,
            align: 'right',
            render: (amount) => (
                <Text strong>{parseInt(amount).toLocaleString()}원</Text>
            ),
        },
        {
            title: '영수증',
            dataIndex: 'receipt_url',
            key: 'receipt_url',
            width: 80,
            align: 'center',
            render: (url) =>
                url ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                        <Text style={{ color: '#52c41a', fontWeight: 'bold', fontSize: 14 }}>O</Text>
                        <Image
                            width={35}
                            height={35}
                            src={getReceiptUrl(url)}
                            placeholder={<PictureOutlined />}
                            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
                            style={{ borderRadius: 4, cursor: 'pointer' }}
                        />
                    </div>
                ) : (
                    <Text style={{ color: '#ff4d4f', fontWeight: 'bold', fontSize: 14 }}>X</Text>
                ),
        },
    ];

    // 승인 대기 컬럼 (작업 버튼 포함)
    const pendingColumns = [
        ...baseColumns,
        {
            title: '작업',
            key: 'action',
            width: 150,
            fixed: 'right',
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        size="small"
                        icon={<CheckOutlined />}
                        onClick={() => handleApprove(record)}
                    >
                        승인
                    </Button>
                    <Button
                        danger
                        size="small"
                        icon={<CloseOutlined />}
                        onClick={() => {
                            setSelectedExpense(record);
                            setRejectModalVisible(true);
                        }}
                    >
                        반려
                    </Button>
                </Space>
            ),
        },
    ];

    // 승인 완료 컬럼 (승인일 추가)
    const approvedColumns = [
        ...baseColumns,
        {
            title: 'PM 승인일',
            dataIndex: 'pm_approved_at',
            key: 'pm_approved_at',
            width: 120,
            render: (date) => dayjs(date).format('YYYY-MM-DD'),
        },
        {
            title: '상태',
            dataIndex: 'status',
            key: 'status',
            width: 120,
            render: (status) => (
                <Tag color="blue">재무 확인 대기</Tag>
            ),
        },
    ];

    return (
        <div>
            <Title level={2}>경비 승인 (PM)</Title>

            <Card style={{ marginTop: 24 }}>
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={[
                        {
                            key: 'pending',
                            label: (
                                <span>
                  승인 대기
                  <Badge
                      count={pendingExpenses.length}
                      style={{ marginLeft: 8, backgroundColor: '#faad14' }}
                  />
                </span>
                            ),
                            children: (
                                <Table
                                    columns={pendingColumns}
                                    dataSource={pendingExpenses}
                                    rowKey="id"
                                    loading={loading}
                                    pagination={{ pageSize: 10 }}
                                    scroll={{ x: 1300 }}
                                    locale={{ emptyText: '승인 대기 중인 경비가 없습니다' }}
                                    expandable={{
                                        expandedRowRender: (record) => (
                                            <div style={{ padding: '12px 24px', background: '#fafafa' }}>
                                                <Space direction="vertical" style={{ width: '100%' }}>
                                                    <div>
                                                        <Text strong>사용 목적:</Text>
                                                        <div style={{ marginTop: 8 }}>{record.purpose}</div>
                                                    </div>
                                                    <div style={{ marginTop: 8 }}>
                                                        <Text strong>계좌 정보:</Text>
                                                        <div style={{ marginTop: 8 }}>
                                                            {record.bank_name} {record.bank_account} ({record.account_holder})
                                                        </div>
                                                    </div>
                                                </Space>
                                            </div>
                                        ),
                                    }}
                                    summary={(data) => {
                                        const totalAmount = data.reduce((sum, item) => sum + parseFloat(item.amount), 0);
                                        return (
                                            <Table.Summary>
                                                <Table.Summary.Row>
                                                    <Table.Summary.Cell index={0} colSpan={6}>
                                                        <Text strong>합계</Text>
                                                    </Table.Summary.Cell>
                                                    <Table.Summary.Cell index={1} align="right">
                                                        <Text strong style={{ fontSize: 16, color: '#cf1322' }}>
                                                            {totalAmount.toLocaleString()}원
                                                        </Text>
                                                    </Table.Summary.Cell>
                                                    <Table.Summary.Cell index={2} />
                                                    <Table.Summary.Cell index={3} />
                                                </Table.Summary.Row>
                                            </Table.Summary>
                                        );
                                    }}
                                />
                            ),
                        },
                        {
                            key: 'approved',
                            label: `승인 완료 (${approvedExpenses.length})`,
                            children: (
                                <Table
                                    columns={approvedColumns}
                                    dataSource={approvedExpenses}
                                    rowKey="id"
                                    loading={loading}
                                    pagination={{ pageSize: 10 }}
                                    scroll={{ x: 1300 }}
                                    locale={{ emptyText: '승인 완료된 경비가 없습니다' }}
                                    expandable={{
                                        expandedRowRender: (record) => (
                                            <div style={{ padding: '12px 24px', background: '#fafafa' }}>
                                                <Space direction="vertical" style={{ width: '100%' }}>
                                                    <div>
                                                        <Text strong>사용 목적:</Text>
                                                        <div style={{ marginTop: 8 }}>{record.purpose}</div>
                                                    </div>
                                                    <div style={{ marginTop: 8 }}>
                                                        <Text strong>계좌 정보:</Text>
                                                        <div style={{ marginTop: 8 }}>
                                                            {record.bank_name} {record.bank_account} ({record.account_holder})
                                                        </div>
                                                    </div>
                                                </Space>
                                            </div>
                                        ),
                                    }}
                                    summary={(data) => {
                                        const totalAmount = data.reduce((sum, item) => sum + parseFloat(item.amount), 0);
                                        return (
                                            <Table.Summary>
                                                <Table.Summary.Row>
                                                    <Table.Summary.Cell index={0} colSpan={6}>
                                                        <Text strong>합계</Text>
                                                    </Table.Summary.Cell>
                                                    <Table.Summary.Cell index={1} align="right">
                                                        <Text strong style={{ fontSize: 16, color: '#cf1322' }}>
                                                            {totalAmount.toLocaleString()}원
                                                        </Text>
                                                    </Table.Summary.Cell>
                                                    <Table.Summary.Cell index={2} />
                                                    <Table.Summary.Cell index={3} />
                                                    <Table.Summary.Cell index={4} />
                                                </Table.Summary.Row>
                                            </Table.Summary>
                                        );
                                    }}
                                />
                            ),
                        },
                    ]}
                />
            </Card>

            {/* 반려 모달 */}
            <Modal
                title="경비 반려"
                open={rejectModalVisible}
                onOk={handleReject}
                onCancel={() => {
                    setRejectModalVisible(false);
                    setRejectionReason('');
                    setSelectedExpense(null);
                }}
                okText="반려"
                cancelText="취소"
                okButtonProps={{ danger: true }}
                width={700}
            >
                {selectedExpense && (
                    <div>
                        {/* 경비 상세 정보 */}
                        <div style={{ marginBottom: 16, padding: 16, backgroundColor: '#f8f9fa', borderRadius: 6 }}>
                            <div style={{ marginBottom: 12, fontWeight: 'bold', fontSize: 16 }}>📋 반려할 경비 정보</div>
                            <p><strong>신청자:</strong> {selectedExpense.user?.name} ({selectedExpense.user?.employee_id})</p>
                            <p><strong>프로젝트:</strong> {selectedExpense.project?.name}</p>
                            <p><strong>지출일:</strong> {dayjs(selectedExpense.expense_date).format('YYYY-MM-DD')}</p>
                            <p><strong>구분:</strong> <Tag color="blue">{categoryMap[selectedExpense.category] || selectedExpense.category}</Tag></p>
                            <p><strong>지출처:</strong> {selectedExpense.vendor}</p>
                            <p><strong>사용목적:</strong> {selectedExpense.purpose}</p>
                            <p><Text strong style={{ fontSize: 16, color: '#ff4d4f' }}>금액: {parseInt(selectedExpense.amount || 0).toLocaleString()}원</Text></p>
                        </div>

                        {/* 영수증 표시 */}
                        {selectedExpense.receipt_url && (
                            <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#fff2e8', borderRadius: 6 }}>
                                <div style={{ marginBottom: 8, fontWeight: 'bold' }}>📄 첨부된 영수증:</div>
                                <Image
                                    src={getReceiptUrl(selectedExpense.receipt_url)}
                                    alt="영수증"
                                    style={{ maxWidth: '100%', maxHeight: 250 }}
                                    placeholder={<PictureOutlined />}
                                    fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
                                    preview={{
                                        mask: (
                                            <div>
                                                <PictureOutlined style={{ marginRight: 4 }} />
                                                크게 보기
                                            </div>
                                        )
                                    }}
                                />
                            </div>
                        )}

                        {/* 반려 사유 입력 */}
                        <div>
                            <div style={{ marginBottom: 8, fontWeight: 'bold', color: '#ff4d4f' }}>
                                ⚠️ 반려 사유 (필수)
                            </div>
                            <TextArea
                                rows={4}
                                placeholder="반려 사유를 구체적으로 입력해주세요. 신청자가 수정할 수 있도록 명확한 이유를 제공해주세요."
                                value={rejectionReason}
                                onChange={(e) => setRejectionReason(e.target.value)}
                                style={{ borderColor: rejectionReason.trim() ? '#d9d9d9' : '#ff4d4f' }}
                            />
                            <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                                💡 예시: "영수증이 불분명합니다", "사용 목적이 프로젝트와 관련이 없습니다", "금액이 과도합니다" 등
                            </div>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default ExpenseApprovalPage;